import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bath,
  Bed,
  Building2,
  Car,
  Layers,
  Loader2,
  MapPin,
  Maximize2,
  Search,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { getPublicPropertyProfiles } from "../../../api/database";

// "Public Properties" - the Database's own property layer.
//
// Christian Gravias, CRM-2026-00031 (2026-09-07): "Every single property needs
// to have a digital profile but as there is so many properties I dont want it
// mixing with our appraisals/current listings as it will just become very
// messy... another category called 'Public Properties' that sits in another
// colour that is a layer lower then the properties the agents add in as
// appraisals, on market or off market listings."
//
// So this page is deliberately its own layer: indigo throughout (the CRM's own
// listings are teal, #55C5D0), fed by prospecting Database rows rather than
// property_management.Property. Nothing here can appear in My Listings, the
// pipelines, the portal feed or the REA feed, by construction.
//
// Named Database* in the tree because the data is Database rows, and because
// PublicPropertiesPage.js is already taken by the external Public Property
// Search (comparable sales) page. The client-facing name is "Public Properties".

const PAGE_SIZE = 60;

// Plain integer formatting. Not `toLocaleString`: the project lints that call
// away because it is almost always a date being rendered in the viewer's own
// timezone rather than Melbourne's.
const numberAU = new Intl.NumberFormat("en-AU");

// Land size arrives from a DecimalField, so "640.00". Number() drops the scale
// nobody measured to.
const sqm = (value) => (value ? `${Number(value)} m²` : "");

const TEMP_CHIP = {
  hot: "bg-rose-50 text-rose-600",
  warm: "bg-amber-50 text-amber-600",
  cold: "bg-sky-50 text-sky-600",
};

export default function DatabasePropertiesPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [suburbs, setSuburbs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaging, setIsPaging] = useState(false);

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [suburb, setSuburb] = useState("all");

  // Typing in the search box must not fire a request per keystroke against a
  // farm area of tens of thousands of rows.
  const debounce = useRef(null);
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(debounce.current);
  }, [search]);

  const load = useCallback(
    async (offset = 0) => {
      if (offset === 0) setIsLoading(true);
      else setIsPaging(true);
      try {
        const res = await getPublicPropertyProfiles({
          search: query || undefined,
          suburb: suburb === "all" ? undefined : suburb,
          limit: PAGE_SIZE,
          offset,
        });
        const data = res.data || {};
        const results = data.results || [];
        setRows((prev) => (offset === 0 ? results : [...prev, ...results]));
        setCount(data.count || 0);
        setHasMore(!!data.has_more);
        // The suburb chips are counted server-side over everything except the
        // suburb filter itself, so they stay stable once one is picked.
        if (offset === 0) setSuburbs(data.suburbs || []);
      } catch {
        toast.error("Could not load Public Properties");
      } finally {
        setIsLoading(false);
        setIsPaging(false);
      }
    },
    [query, suburb]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] min-h-screen -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/properties")}
              className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 text-[#374151]" />
            </button>
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <Layers className="w-5 h-5 text-indigo-500" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-black text-[#374151] tracking-tighter">
                  Public Properties
                </h1>
                <p className="text-xs text-gray-400 font-medium">
                  Every property in your Database, as its own profile. Kept separate from your
                  appraisals and listings.
                </p>
              </div>
            </div>
            <span className="ml-auto hidden sm:inline-flex px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-600 text-xs font-black">
              {numberAU.format(count)} {count === 1 ? "property" : "properties"}
            </span>
          </div>

          <div className="mt-4 relative">
            <Search className="w-4 h-4 text-gray-300 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search an address, street, suburb, postcode or owner"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"
            />
          </div>

          {suburbs.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              <Chip active={suburb === "all"} onClick={() => setSuburb("all")}>
                All suburbs
              </Chip>
              {suburbs.map((s) => (
                <Chip
                  key={s.suburb}
                  active={suburb === s.suburb}
                  onClick={() => setSuburb(suburb === s.suburb ? "all" : s.suburb)}
                >
                  {s.suburb} <span className="opacity-60">{s.count}</span>
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 md:px-8 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-400" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-400">
              {query || suburb !== "all" ? "Nothing matches that" : "No Public Properties yet"}
            </h3>
            <p className="text-sm text-gray-300 mt-1 max-w-md">
              {query || suburb !== "all"
                ? "Try a different address or suburb."
                : "Every owner row you import into the Database with an address becomes a property profile here."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {rows.map((p) => (
                <button
                  key={p.record_id}
                  onClick={() => navigate(`/public-properties/${p.record_id}`)}
                  className="text-left relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all p-4 pl-6"
                >
                  <span
                    className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-400"
                    aria-hidden="true"
                  />
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-300 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 text-[15px] truncate">{p.address}</p>
                      {p.suburb && (
                        <p className="text-xs text-gray-400 truncate flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {p.suburb} {p.postcode}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-gray-500">
                        {p.beds != null && (
                          <span className="flex items-center gap-1">
                            <Bed className="w-3.5 h-3.5 text-gray-300" /> {p.beds}
                          </span>
                        )}
                        {p.baths != null && (
                          <span className="flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5 text-gray-300" /> {p.baths}
                          </span>
                        )}
                        {p.car != null && (
                          <span className="flex items-center gap-1">
                            <Car className="w-3.5 h-3.5 text-gray-300" /> {p.car}
                          </span>
                        )}
                        {p.land_sqm && (
                          <span className="flex items-center gap-1">
                            <Maximize2 className="w-3.5 h-3.5 text-gray-300" /> {sqm(p.land_sqm)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                        <span
                          title="A property profile built from a Database row. One layer below your appraisals and listings."
                          className="inline-block px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-bold uppercase tracking-wider"
                        >
                          Public Property
                        </span>
                        {p.property_type && (
                          <span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
                            {p.property_type}
                          </span>
                        )}
                        {p.lead_temp && (
                          <span
                            className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                              TEMP_CHIP[p.lead_temp] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {p.lead_temp}
                          </span>
                        )}
                      </div>
                      {p.owner_name && (
                        <p className="text-[11px] text-gray-400 mt-2 truncate flex items-center gap-1">
                          <User className="w-3 h-3" /> {p.owner_name}
                          {p.owner_count > 1 && " (2 owners)"}
                        </p>
                      )}
                      {p.category && (
                        <p className="text-[11px] text-gray-300 mt-0.5 truncate">{p.category}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => load(rows.length)}
                  disabled={isPaging}
                  className="px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50"
                >
                  {isPaging && <Loader2 className="w-4 h-4 animate-spin" />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-colors ${
        active
          ? "bg-indigo-500 border-indigo-500 text-white"
          : "bg-white border-gray-200 text-gray-500 hover:border-indigo-300"
      }`}
    >
      {children}
    </button>
  );
}
